import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useCallback} from 'react';
import {useColorScheme} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'extensions/navigation';
import {useModal} from 'extensions/viewport/useModal';
import {Choice} from 'interface/base/Choice';
import {selectors} from 'store/slices/files';
import {Store} from 'store/slices/files/types';
import {vectors, colors} from 'config/styles';

import * as effects from 'interface/hooks/effects/files';

export function useModalSort(folderKey: string) {
  const {t} = useLingui();
  const scheme = useColorScheme();
  const modal = useModal();
  const sort = useSelector(selectors.getSort);
  const filter = useSelector(selectors.getFilter);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const select = useCallback((category: Store['sort']['category']) => {
    const order = sort.order === 'asc' ? 'desc' : 'asc';
    effects.sort(navigate, dispatch, folderKey)({category, order}, filter);
    modal.exit();
  }, [folderKey, sort, navigate, dispatch]);

  const icon = useCallback((isActive: boolean) => 
    <Svg viewBox="0 0 24 24" width={22} height={22}>
      <Path
        d={sort.order === 'asc'
          ? vectors.icons.arrowUp
          : vectors.icons.arrowDown
        }
        fill={isActive
          ? scheme === 'dark'
            ? colors.neutral.white
            : colors.neutral.darkest
          : 'transparent'}
      />
    </Svg>
  , [sort]);

  const open = useCallback(() => {
    modal.open(
      <Choice
        title={t`Sort Folder`}
        buttons={[
          {
            text: t`Sort by Name`,
            icon: icon(sort.category === 'name'),
            onPress: () => select('name'),
          },
          {
            text: t`Sort by Date`,
            icon: icon(sort.category === 'date'),
            onPress: () => select('date'),
          },
          {
            text: t`Sort by Size`,
            icon: icon(sort.category === 'size'),
            onPress: () => select('size'),
          },
          {
            text: t`Sort by Downloads`,
            icon: icon(sort.category === 'downloads'),
            onPress: () => select('downloads'),
          },
          {
            text: t`Cancel`,
            icon: icon(false),
            cancel: true,
            onPress: modal.exit,
          },
        ]}
      />
    );
  }, [sort]);

  return {open, exit: modal.exit};
}
